import { useEffect, useRef, useState } from "react";
import { api, API, formatError } from "@/lib/api";
import { useBranding } from "@/context/BrandingContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Upload, Trash2, Eye, RotateCcw, Loader2, Send } from "lucide-react";

const DEFAULT_PRIMARY = "#2563EB";
const DEFAULT_ACCENT = "#16A34A";
const HEX_RE = /^#[0-9A-Fa-f]{6}$/;

/**
 * Branding card - shown inside the Company Admin Settings page.
 *
 * Lets the admin upload a logo (PNG/JPG, ≤ 1 MB), pick the brand primary and
 * accent colours, and set an optional 1-3 line email signature appended to
 * outbound transactional emails. Saves trigger a refresh of the BrandingProvider
 * so the AppShell logo + nav theme update in place.
 */
export default function BrandingCard() {
  const { branding, refresh } = useBranding();
  const [primary, setPrimary] = useState(DEFAULT_PRIMARY);
  const [accent, setAccent] = useState(DEFAULT_ACCENT);
  const [signature, setSignature] = useState("");
  const [logoUrl, setLogoUrl] = useState(null);
  const [logoFileId, setLogoFileId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [testing, setTesting] = useState(false);
  const fileInputRef = useRef(null);

  // Seed local state from server-provided branding once it arrives.
  useEffect(() => {
    setPrimary(branding.primary_color || DEFAULT_PRIMARY);
    setAccent(branding.accent_color || DEFAULT_ACCENT);
    setSignature(branding.email_signature || "");
    setLogoUrl(branding.logo_url || null);
    setLogoFileId(branding.logo_file_id || null);
  }, [branding.primary_color, branding.accent_color, branding.email_signature,
      branding.logo_url, branding.logo_file_id]);

  const dirty =
    primary.toUpperCase() !== (branding.primary_color || DEFAULT_PRIMARY).toUpperCase() ||
    accent.toUpperCase()  !== (branding.accent_color  || DEFAULT_ACCENT).toUpperCase() ||
    (signature || "")     !== (branding.email_signature || "");

  const save = async () => {
    if (!HEX_RE.test(primary) || !HEX_RE.test(accent)) {
      toast.error("Colours must be hex like #2563EB."); return;
    }
    setBusy(true);
    try {
      await api.put("/branding", { primary_color: primary, accent_color: accent,
                                    email_signature: signature || "" });
      await refresh();
      toast.success("Branding saved.");
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || "Could not save branding.");
    } finally { setBusy(false); }
  };

  const resetDefaults = () => {
    setPrimary(DEFAULT_PRIMARY);
    setAccent(DEFAULT_ACCENT);
    setSignature("");
  };

  const onPickFile = async (file) => {
    if (!file) return;
    if (!/(image\/png|image\/jpeg)/i.test(file.type)) {
      toast.error("Only PNG or JPG files are accepted."); return;
    }
    if (file.size > 1024 * 1024) {
      toast.error("Logo file must be 1 MB or smaller."); return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await fetch(`${API}/branding/logo`, {
        method: "POST", body: form, credentials: "include",
      });
      const body = await res.json();
      if (!res.ok) {
        toast.error(formatError(body.detail) || `Upload failed (${res.status}).`);
        return;
      }
      setLogoUrl(body.logo_url);
      setLogoFileId(body.logo_file_id);
      await refresh();
      toast.success("Logo uploaded.");
    } catch (e) {
      toast.error("Logo upload failed.");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const removeLogo = async () => {
    if (!logoFileId) return;
    setUploading(true);
    try {
      await api.delete("/branding/logo");
      setLogoUrl(null); setLogoFileId(null);
      await refresh();
      toast.success("Logo removed.");
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || "Could not remove logo.");
    } finally { setUploading(false); }
  };

  const sendTestEmail = async () => {
    if (dirty) {
      toast.error("Save your branding first so the test email reflects your latest changes.");
      return;
    }
    setTesting(true);
    try {
      const { data } = await api.post("/branding/test-email");
      if (data?.delivered === false) {
        toast.message("Test email queued, but the delivery service reported a failure. Check the audit log for details.");
      } else {
        toast.success(`Test email sent to ${data?.to || "your inbox"}. Check spam if you do not see it within a minute.`);
      }
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || "Could not send test email.");
    } finally { setTesting(false); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 sm:p-7" data-testid="branding-card">
      <div className="flex items-center justify-between gap-4 mb-1">
        <div className="eyebrow">Branding</div>
        <button type="button" onClick={resetDefaults} data-testid="branding-reset-btn"
          className="text-xs text-[#4B5773] hover:text-[#0B1A36] flex items-center gap-1.5">
          <RotateCcw className="h-3 w-3"/> Reset to defaults
        </button>
      </div>
      <h3 className="font-display text-2xl mb-1">Make NextRota feel like your company</h3>
      <p className="text-xs text-[#4B5773] mb-5">
        Your logo, colours and signature apply across the in-app workspace, outgoing emails and PDF exports.
      </p>

      {/* Logo */}
      <div className="grid sm:grid-cols-2 gap-5">
        <div>
          <Label className="eyebrow">Company logo</Label>
          <div className="mt-1.5 border border-slate-200 rounded-lg p-4 flex items-center gap-4 min-h-[88px]">
            {logoUrl ? (
              <img src={logoUrl} alt="Tenant logo"
                   data-testid="branding-logo-preview"
                   className="max-h-12 max-w-[180px] object-contain"/>
            ) : (
              <div className="h-12 w-12 rounded-md bg-slate-50 border border-slate-200 grid place-items-center text-[10px] text-[#4B5773] uppercase tracking-wider">
                None
              </div>
            )}
            <div className="flex-1 flex flex-wrap gap-2">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg"
                className="hidden"
                data-testid="branding-logo-input"
                onChange={(e) => onPickFile(e.target.files?.[0])}
              />
              <Button
                type="button"
                variant="outline"
                disabled={uploading}
                onClick={() => fileInputRef.current?.click()}
                data-testid="branding-logo-upload-btn"
                className="gap-2"
              >
                {uploading ? <Loader2 className="h-4 w-4 animate-spin"/> : <Upload className="h-4 w-4"/>}
                {logoUrl ? "Replace logo" : "Upload logo"}
              </Button>
              {logoUrl && (
                <Button type="button" variant="ghost" disabled={uploading}
                  onClick={removeLogo} data-testid="branding-logo-remove-btn"
                  className="gap-2 text-red-600 hover:text-red-700">
                  <Trash2 className="h-4 w-4"/> Remove
                </Button>
              )}
            </div>
          </div>
          <p className="text-[11px] text-[#4B5773] mt-1.5">PNG or JPG, 1 MB max. Transparent PNG works best on the in-app header.</p>
        </div>

        {/* Colour pickers */}
        <div className="space-y-3">
          <ColourField
            label="Primary colour"
            testId="branding-primary"
            value={primary}
            onChange={setPrimary}
            description="Buttons, links and the active nav state."
          />
          <ColourField
            label="Accent colour"
            testId="branding-accent"
            value={accent}
            onChange={setAccent}
            description="Success states, completed steps and key highlights."
          />
        </div>
      </div>

      {/* Signature */}
      <div className="mt-5">
        <Label className="eyebrow">Email signature</Label>
        <Textarea
          rows={3}
          maxLength={240}
          value={signature}
          onChange={(e) => setSignature(e.target.value)}
          data-testid="branding-signature-input"
          placeholder="e.g. Acme Logistics Ltd · 21 Old Street, London · 020 7123 4567"
          className="mt-1.5"
        />
        <p className="text-[11px] text-[#4B5773] mt-1.5">
          Up to 240 characters. Appears at the bottom of every outbound NextRota email. Leave blank for the standard footer.
        </p>
      </div>

      {/* Live preview row */}
      <div className="mt-6 rounded-lg border border-slate-200 overflow-hidden" data-testid="branding-preview">
        <div className="px-4 py-3 text-xs uppercase tracking-wider text-[#4B5773] border-b border-slate-200 bg-slate-50 flex items-center gap-2">
          <Eye className="h-3.5 w-3.5"/> Live preview
        </div>
        <div className="p-5 bg-white">
          <div className="flex flex-wrap items-center gap-3 mb-4">
            {logoUrl
              ? <img src={logoUrl} alt="" className="h-7 w-auto max-w-[160px] object-contain"/>
              : <div className="text-xs uppercase tracking-widest text-[#4B5773] font-semibold">Your logo here</div>}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center px-3 py-1.5 rounded-md text-white text-sm font-medium" style={{ background: primary }}>
              Primary button
            </span>
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border" style={{ borderColor: accent, color: accent, background: `${accent}15` }}>
              Accent pill
            </span>
            <a href="#" onClick={(e)=>e.preventDefault()} className="text-sm underline" style={{ color: primary }}>An example link</a>
          </div>
        </div>
      </div>

      <div className="mt-6 flex items-center gap-3 flex-wrap">
        <Button onClick={save} disabled={busy || !dirty}
          data-testid="branding-save-btn"
          className="rounded-lg bg-[#2563EB] hover:bg-[#1D4ED8]">
          {busy ? "Saving…" : "Save branding"}
        </Button>
        <Button type="button" variant="outline" onClick={sendTestEmail}
          disabled={testing || dirty}
          data-testid="branding-test-email-btn"
          className="rounded-lg gap-2"
          title={dirty ? "Save your branding first" : "Send a sample branded email to your inbox"}>
          {testing ? <Loader2 className="h-4 w-4 animate-spin"/> : <Send className="h-4 w-4"/>}
          {testing ? "Sending…" : "Send test email"}
        </Button>
        {!dirty && !testing && (
          <span className="text-xs text-[#4B5773]">A sample branded email will arrive in your inbox.</span>
        )}
        {dirty && (
          <span className="text-xs text-amber-700">Save first - the test email always uses the saved branding.</span>
        )}
      </div>
    </div>
  );
}

function ColourField({ label, value, onChange, testId, description }) {
  const valid = HEX_RE.test(value);
  return (
    <div>
      <Label className="eyebrow">{label}</Label>
      <div className="mt-1.5 flex items-center gap-2">
        <input
          type="color"
          value={valid ? value : "#000000"}
          onChange={(e) => onChange(e.target.value.toUpperCase())}
          data-testid={`${testId}-picker`}
          className="h-10 w-12 rounded-md border border-slate-200 cursor-pointer p-0 bg-white"
          aria-label={`${label} colour picker`}
        />
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          data-testid={`${testId}-hex`}
          placeholder="#2563EB"
          className={`h-10 rounded-md font-mono uppercase ${valid ? "" : "border-red-300"}`}
          maxLength={7}
        />
      </div>
      <p className="text-[11px] text-[#4B5773] mt-1">{description}</p>
    </div>
  );
}
